import { CaseStudy } from 'types/generated/contentful'
import { Card } from 'designsystem/Card'
import ArrowDownHeavy from 'icons/ArrowDownHeavy'
import CaseStudyQuote from './CaseStudyQuote'
import IconRenderer from './IconRenderer'
import {
  CostReductionTooltip,
  FasterResponseTimeTooltip,
  LessApiTrafficTooltip,
} from './ImprovementStats'
import cn from 'classnames'

interface CaseStudyInfoHeroProps extends React.HTMLAttributes<HTMLDivElement> {
  caseStudy: CaseStudy
}

const CaseStudyInfoHero = ({
  caseStudy,
  className,
  ...props
}: CaseStudyInfoHeroProps): JSX.Element => {
  const { quote } = caseStudy

  return (
    <div className={cn('rounded-1 px-2 py-2', className)} {...props}>
      <Card
        noPadding
        noMaxWidth
        className='grid md:grid-cols-2 lg:grid-cols-3 lg:grid-rows-2'
      >
        {/* Quote */}
        {quote && (
          <CaseStudyQuote
            className='flex flex-col justify-between p-8 border-b border-neutral-7 md:col-span-3 lg:col-span-2 lg:row-span-3 lg:border-r lg:border-b-0 text-24'
            quote={quote}
          />
        )}

        {/* Increased performance */}
        <div
          className={cn(
            'relative p-8 border-b border-neutral-7 flex items-center space-x-3 md:border-b-0 md:border-r lg:border-r-0',
            {
              'case-study-highlighted':
                caseStudy.improvementType === 'increased-performance',
            },
          )}
        >
          <IconRenderer
            icon='increased-performance'
            className={cn('h-10', {
              'text-neutral':
                caseStudy.improvementType !== 'increased-performance',
            })}
          />

          <div>
            <div className='flex space-x-2 items-center'>
              <ArrowDownHeavy className='text-green transform rotate-180' />
              <span className='large-body-text'>
                {caseStudy.increasedPerformance}%
              </span>
            </div>
            <div className='flex items-center gap-1.5'>
              <p className='small-body-text opacity-60'>
                Faster p50 API response time
              </p>
              <FasterResponseTimeTooltip
                parentBgColor={
                  caseStudy.improvementType === 'increased-performance'
                    ? 'blue'
                    : 'white'
                }
              />
            </div>
          </div>
        </div>

        {/* Cost reduction (optional) */}
        {caseStudy.costReduction != null && (
          <div
            className={cn(
              'p-8 border-b border-neutral-7 flex items-center space-x-3 md:border-b-0 lg:border-t',
              {
                'case-study-highlighted':
                  caseStudy.improvementType === 'cost-reduction',
              },
            )}
          >
            <IconRenderer
              icon='cost-reduction'
              className={cn('h-10', {
                'text-blue': caseStudy.improvementType !== 'cost-reduction',
              })}
            />

            <div>
              <div className='flex space-x-2 items-center'>
                <ArrowDownHeavy className='text-green' />
                <span className='large-body-text'>
                  {caseStudy.costReduction}%
                </span>
              </div>
              <div className='flex items-center gap-1.5'>
                <p className='small-body-text opacity-60'>
                  Reduction in Cloud Cost
                </p>
                <CostReductionTooltip
                  parentBgColor={
                    caseStudy.improvementType === 'cost-reduction'
                      ? 'blue'
                      : 'white'
                  }
                />
              </div>
            </div>
          </div>
        )}

        {/* API traffic reduction */}
        <div
          className={cn(
            'p-8 border-b border-neutral-7 flex items-center space-x-3 md:border-b-0 lg:border-t',
            {
              'case-study-highlighted':
                caseStudy.improvementType === 'server-load-reduction',
            },
          )}
        >
          <IconRenderer
            icon='server-load-reduction'
            className={cn('h-10', {
              'text-blue':
                caseStudy.improvementType !== 'server-load-reduction',
            })}
          />

          <div>
            <div className='flex space-x-2 items-center'>
              <ArrowDownHeavy className='text-green' />
              <span className='large-body-text'>
                {caseStudy.serverLoadReduction}%
              </span>
            </div>
            <div className='flex items-center gap-1.5'>
              <p className='small-body-text opacity-60'>Less API traffic</p>
              <LessApiTrafficTooltip
                parentBgColor={
                  caseStudy.improvementType === 'server-load-reduction'
                    ? 'blue'
                    : 'white'
                }
              />
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default CaseStudyInfoHero
